'use strict';

const SELECTOR = '.js-jobs-iframe';
const FRAME_SELECTOR = '#myDoutaz-jobList';
const LOADER_SELECTOR = '.loader-wrapper';
const SUCCESS_SELECTOR = '.success';
const HIDDEN_CLASS = 'hidden';
const MIN_VALID_HEIGHT = 200;

const parseHeightDeep = (payload) => {
  if (payload == null) {
    return null;
  }

  if (typeof payload === 'string') {
    // iframe-resizer style: [iFrameSizer]id:height:width:type
    if (payload.startsWith('[iFrameSizer]')) {
      const parts = payload.slice('[iFrameSizer]'.length).split(':');
      const parsed = Number.parseInt(parts[1], 10);
      return Number.isFinite(parsed) && parsed >= MIN_VALID_HEIGHT ? parsed : null;
    }
    return null;
  }

  if (typeof payload === 'object') {
    const commonCandidates = [
      payload.height,
      payload.iframeHeight,
      payload.scrollHeight,
      payload.clientHeight,
      payload.offsetHeight,
      payload.h,
      payload.value
    ];

    for (const candidate of commonCandidates) {
      const parsed = Number.parseInt(candidate, 10);
      if (Number.isFinite(parsed) && parsed >= MIN_VALID_HEIGHT) {
        return parsed;
      }
    }

    for (const value of Object.values(payload)) {
      const parsed = parseHeightDeep(value);
      if (parsed) {
        return parsed;
      }
    }
  }

  return null;
};

const parseAction = (payload) => {
  if (typeof payload === 'string') {
    if (payload === 'clicked' || payload === 'ready' || payload === 'post') {
      return payload;
    }
    if (payload.startsWith('[iFrameSizer]') && payload.includes(':message:')) {
      const raw = payload.slice('[iFrameSizer]'.length);
      const marker = ':message:';
      const markerIndex = raw.indexOf(marker);
      if (markerIndex !== -1) {
        try {
          const message = JSON.parse(raw.slice(markerIndex + marker.length));
          return parseAction(message);
        } catch (error) {
          return '';
        }
      }
    }
  }

  if (payload && typeof payload === 'object') {
    if (typeof payload.message === 'string') {
      return parseAction(payload.message);
    }
    if (typeof payload.action === 'string') {
      return parseAction(payload.action);
    }
    if (payload.data !== undefined) {
      return parseAction(payload.data);
    }
  }

  return '';
};

const createIFrameResize = () => (options = {}, targetSelector = 'iframe') => {
  const frames = Array.from(document.querySelectorAll(targetSelector))
    .filter((node) => node instanceof HTMLIFrameElement);

  const allowedOrigins = options.checkOrigin === false
    ? null
    : frames
      .map((frame) => {
        try {
          return new URL(frame.src, window.location.origin).origin;
        } catch (error) {
          return null;
        }
      })
      .filter(Boolean);

  const onMessage = (event) => {
    if (Array.isArray(allowedOrigins) && allowedOrigins.length && !allowedOrigins.includes(event.origin)) {
      return;
    }

    const height = parseHeightDeep(event.data);
    if (height) {
      frames.forEach((frame) => {
        frame.style.height = `${height}px`;
      });
    }

    if (typeof options.messageCallback === 'function') {
      const action = parseAction(event.data);
      if (action) {
        options.messageCallback({ message: action });
      }
    }
  };

  frames.forEach((frame) => {
    frame.addEventListener('load', () => {
      if (typeof options.initCallback === 'function') {
        options.initCallback();
      }
    }, { once: true });
  });

  window.addEventListener('message', onMessage);
  return frames;
};

const iFrameResize = createIFrameResize();

const initOne = (root) => {
  if (!(root instanceof Element) || root.dataset.jobsIframeInitialized === '1') {
    return;
  }

  const frame = root.querySelector(FRAME_SELECTOR);
  const loader = root.querySelector(LOADER_SELECTOR);
  const confirmation = root.querySelector(SUCCESS_SELECTOR);

  if (!(frame instanceof HTMLIFrameElement) || !(loader instanceof Element) || !(confirmation instanceof Element)) {
    return;
  }

  let isPost = false;

  const handleView = () => {
    loader.classList.add(HIDDEN_CLASS);
    frame.classList.remove(HIDDEN_CLASS);
  };

  iFrameResize(
    {
      log: true,
      checkOrigin: false,
      initCallback: handleView,
      messageCallback: (message) => {
        const action = message.message;

        if (action === 'clicked') {
          confirmation.classList.add(HIDDEN_CLASS);
          loader.classList.remove(HIDDEN_CLASS);
          frame.classList.add(HIDDEN_CLASS);
        }

        if (action === 'ready') {
          if (isPost) {
            confirmation.classList.remove(HIDDEN_CLASS);
            isPost = false;
          }
          frame.classList.remove(HIDDEN_CLASS);
          loader.classList.add(HIDDEN_CLASS);
        }

        if (action === 'post') {
          isPost = true;
          confirmation.classList.add(HIDDEN_CLASS);
          loader.classList.remove(HIDDEN_CLASS);
          frame.classList.add(HIDDEN_CLASS);
        }
      }
    },
    FRAME_SELECTOR
  );

  root.dataset.jobsIframeInitialized = '1';
};

const init = () => {
  const roots = document.querySelectorAll(SELECTOR);
  roots.forEach(initOne);
};

export default { init };
